import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, useWindowDimensions, ScrollView } from 'react-native';
import { FormSection } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { DESKTOP_COLORS, DESKTOP_STYLES } from '@/constants/admin';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';
import { IUserState, IOrganization, IProperty } from '@/types/admin/navigation';
import { MultiSelectPicker } from '@/components';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { useAnchoredPosition } from '@/hooks/common/useAnchoredPosition';
import { useResponsive } from '@/hooks/common/useResponsive';
import { SVG_COMPONENTS } from '@/assets/admin';

interface UserRoleSelectionSectionProps {
  formData: {
    roleName: string;
    organizations?: string[];
    properties?: string[];
  };
  errors: Record<string, string>;
  onFieldChange: (field: string, value: any) => void;
  adminUser: IUserState;
}

export const UserRoleSelectionSection: React.FC<UserRoleSelectionSectionProps> = ({
  formData,
  errors,
  onFieldChange,
  adminUser,
}) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  // Get available roles based on current admin user's permissions
  const availableRoles = useMemo(() => {
    const userRoles = adminUser.roles?.map(role => role.name) || [];
    const roles: any[] = [];

    // Super Admin can create all roles
    if (userRoles.includes(roleNamesAndDescriptions.superAdmin.value)) {
      roles.push(
        roleNamesAndDescriptions.superAdmin,
        roleNamesAndDescriptions.orgAdmin,
        roleNamesAndDescriptions.billingAdmin,
        roleNamesAndDescriptions.propertyAdmin
      );
    }
    // Org Admin can create billing and property admins
    else if (userRoles.includes(roleNamesAndDescriptions.orgAdmin.value)) {
      roles.push(
        roleNamesAndDescriptions.orgAdmin,
        roleNamesAndDescriptions.billingAdmin,
        roleNamesAndDescriptions.propertyAdmin
      );
    }
    // Billing Admin can create property admins
    else if (userRoles.includes(roleNamesAndDescriptions.billingAdmin.value)) {
      roles.push(
        roleNamesAndDescriptions.billingAdmin,
        roleNamesAndDescriptions.propertyAdmin
      );
    }
    // Property Admin can create property admins
    else if (userRoles.includes(roleNamesAndDescriptions.propertyAdmin.value)) {
      roles.push(roleNamesAndDescriptions.propertyAdmin);
    }

    return roles;
  }, [adminUser.roles]);

  // Organization options
  const organizationOptions = useMemo(() => {
    return (adminUser.organizations || []).map((org: IOrganization) => ({
      label: org.name,
      value: org.name,
    }));
  }, [adminUser.organizations]);

  // Property options
  const propertyOptions = useMemo(() => {
    return (adminUser.properties || []).map((prop: IProperty) => ({
      label: prop.marketingName,
      value: prop.marketingName,
    }));
  }, [adminUser.properties]);

  // Show organization selector for org admin
  const showOrganizationSelector = formData.roleName === roleNamesAndDescriptions.orgAdmin.value;
  
  // Show property selector for property and billing admins
  const showPropertySelector = [
    roleNamesAndDescriptions.propertyAdmin.value,
    roleNamesAndDescriptions.billingAdmin.value,
  ].includes(formData.roleName);

  return (
    <>
      {/* Step Indicator */}
      <View style={styles.stepIndicator}>
        <Text style={styles.stepText}>
          Step 2 of 3: Assign User Permissions
        </Text>
      </View>

      <FormSection 
        title="User Permissions"
        subtitle="Select the role and access permissions for this user"
      >
      <View style={styles.container}>
        {/* Role Selection */}
        <View style={styles.roleSection}>
          <Text style={styles.sectionTitle}>User Role</Text>
          {errors.roleName && <Text style={styles.errorText}>{errors.roleName}</Text>}
          
          <View style={styles.roleOptions}>
            {availableRoles.map((role: any) => (
              <RoleOption
                key={role.value}
                role={role}
                isSelected={formData.roleName === role.value}
                onSelect={() => onFieldChange('roleName', role.value)}
              />
            ))}
          </View>
        </View>

        {/* Organization Selector */}
        {showOrganizationSelector && (
          <View style={styles.selectorSection}>
            {isDesktop ? (
              <DesktopMultiSelect
                label="Related Organizations"
                options={organizationOptions}
                selectedValues={formData.organizations || []}
                onSelectionChange={(values: string[]) => onFieldChange('organizations', values)}
                placeholder="Select organizations"
                error={errors.organizations}
                required
              />
            ) : (
              <MultiSelectPicker
                label="Related Organizations"
                options={organizationOptions}
                selectedValues={formData.organizations || []}
                onSelectionChange={(values: string[]) => onFieldChange('organizations', values)}
                placeholder="Select organizations"
                error={errors.organizations}
                required
              />
            )}
          </View>
        )}

        {/* Property Selector */}
        {showPropertySelector && (
          <View style={styles.selectorSection}>
            {isDesktop ? (
              <DesktopMultiSelect
                label="Related Properties"
                options={propertyOptions}
                selectedValues={formData.properties || []}
                onSelectionChange={(values: string[]) => onFieldChange('properties', values)}
                placeholder="Select properties"
                error={errors.properties}
                required
              />
            ) : (
              <MultiSelectPicker
                label="Related Properties"
                options={propertyOptions}
                selectedValues={formData.properties || []}
                onSelectionChange={(values: string[]) => onFieldChange('properties', values)}
                placeholder="Select properties"
                error={errors.properties}
                required
              />
            )}
          </View>
        )}
      </View>
      </FormSection>
    </>
  );
};

interface RoleOptionProps {
  role: {
    name: string;
    value: string;
    description: string;
  };
  isSelected: boolean;
  onSelect: () => void;
}

const RoleOption: React.FC<RoleOptionProps> = ({ role, isSelected, onSelect }) => {
  return (
    <TouchableOpacity
      style={[styles.roleOption, isSelected && styles.roleOptionSelected]}
      onPress={onSelect}
      activeOpacity={0.7}
    >
      <View style={styles.roleOptionContent}>
        <View style={styles.roleOptionHeader}>
          <Text style={[styles.roleOptionTitle, isSelected && styles.roleOptionTitleSelected]}>
            {role.name}
          </Text>
          <View style={[styles.radioButton, isSelected && styles.radioButtonSelected]}>
            {isSelected && <View style={styles.radioButtonInner} />}
          </View>
        </View>
        <Text style={[styles.roleOptionDescription, isSelected && styles.roleOptionDescriptionSelected]}>
          {role.description}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  stepIndicator: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    marginBottom: 16,
  },
  stepText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
  container: {
    gap: 24,
  },
  roleSection: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.ERROR,
    marginTop: -8,
  },
  roleOptions: {
    gap: 12,
  },
  roleOption: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    padding: 16,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
  },
  roleOptionSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
    backgroundColor: ADMIN_COLORS.PRIMARY + '10',
  },
  roleOptionContent: {
    gap: 8,
  },
  roleOptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roleOptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  roleOptionTitleSelected: {
    color: ADMIN_COLORS.PRIMARY,
  },
  roleOptionDescription: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 20,
  },
  roleOptionDescriptionSelected: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  radioButtonSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  selectorSection: {
    gap: 8,
  },
});

// Desktop multi-select with dropdown panel (renders in a Modal to escape clipping)
const DesktopMultiSelect = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
  placeholder,
  error,
  required,
}: {
  label: string;
  options: Array<{ label: string; value: string }>;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const triggerRef = React.useRef<View>(null);
  const pos = useAnchoredPosition(anchor, { width: Math.min(360, (anchor?.w || 280)), maxHeight: 320 });

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter(v => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  const displayText = selectedValues.length > 0
    ? `${selectedValues.length} selected`
    : (placeholder || 'Select');

  return (
    <View style={{ gap: 6 }}>
      <Text style={[styles.sectionTitle, { fontSize: 14 }]}>
        {label}{required ? ' *' : ''}
      </Text>
      {error ? (
        <Text style={{ fontSize: 12, color: ADMIN_COLORS.ERROR, marginTop: -4 }}>{error}</Text>
      ) : null}
      <View style={{ position: 'relative' }} ref={triggerRef}>
        <TouchableOpacity
          onPress={() => {
            const node: any = triggerRef.current;
            if (node && node.measureInWindow) {
              node.measureInWindow((x: number, y: number, w: number, h: number) => {
                setAnchor({ x, y, w, h });
                setOpen(true);
              });
            } else {
              setOpen(true);
            }
          }}
          activeOpacity={0.7}
          style={{
            borderWidth: 1,
            borderColor: DESKTOP_COLORS.TABLE.BORDER,
            backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
            borderRadius: 6,
            paddingHorizontal: 12,
            paddingVertical: 10,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Text style={DESKTOP_STYLES.primaryText}>{displayText}</Text>
          {open ? (
            <SVG_COMPONENTS.CHEVRON_UP width={14} height={14} color={DESKTOP_COLORS.TABLE.TEXT_SECONDARY} />
          ) : (
            <SVG_COMPONENTS.CHEVRON_DOWN width={14} height={14} color={DESKTOP_COLORS.TABLE.TEXT_SECONDARY} />
          )}
        </TouchableOpacity>
      </View>

      {open && anchor && (
        <Modal transparent visible onRequestClose={() => setOpen(false)}>
          <TouchableOpacity activeOpacity={1} onPress={() => setOpen(false)} style={{ flex: 1 }}>
            <View style={{ flex: 1 }}>
              <AnchoredDropdown isOpen={open} anchor={anchor} width={Math.min(anchor.w, 360)} maxHeight={320} onRequestClose={() => setOpen(false)}>
                <ScrollView style={{ maxHeight: 320 }}>
                  {options.map((opt) => {
                    const isSelected = selectedValues.includes(opt.value);
                    return (
                      <TouchableOpacity
                        key={`${opt.label}-${opt.value}`}
                        style={{
                          paddingHorizontal: 12,
                          paddingVertical: 10,
                          borderBottomWidth: 1,
                          borderBottomColor: DESKTOP_COLORS.TABLE.BORDER,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
                        }}
                        onPress={() => toggleValue(opt.value)}
                        activeOpacity={0.7}
                      >
                        <Text style={DESKTOP_STYLES.primaryText}>{opt.label}</Text>
                        {isSelected && (
                          <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color={DESKTOP_COLORS.BUTTON.PRIMARY_BACKGROUND} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  gap: 8,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderTopWidth: 1,
                  borderTopColor: DESKTOP_COLORS.TABLE.BORDER,
                  backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
                }}>
                  <TouchableOpacity
                    onPress={() => onSelectionChange([])}
                    style={{
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                      borderRadius: 6,
                      borderWidth: 1,
                      borderColor: DESKTOP_COLORS.TABLE.BORDER,
                      backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
                    }}
                  >
                    <Text style={DESKTOP_STYLES.secondaryText}>Clear all</Text>
                  </TouchableOpacity>
                </View>
              </AnchoredDropdown>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </View>
  );
};
